import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
// Api
import {
  ADD_STRIPE_PAYMENT_METHOD,
  SET_PAYMENT_METHOD_AS_DEFAULT,
} from 'api/payments/mutations';
import { PaymentFields } from 'api/payments/fragments';
import { MY_PAYMENT_METHODS } from 'api/payments/queries';
// Helpers
import { yupStringRequiredValidation } from 'helpers/validation';
import { getStripeElementOptions } from 'helpers/payments';
// Types
import {
  AddStripePaymentMethod,
  AddStripePaymentMethodVariables,
} from 'api/payments/types/AddStripePaymentMethod';
import {
  SetPaymentMethodAsDefault,
  SetPaymentMethodAsDefaultVariables,
} from 'api/payments/types/SetPaymentMethodAsDefault';
import {
  AddPaymentModalProps,
  PaymentFormInputs,
} from 'helpers/payments.types';
// Ui
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Modal from 'ui3/Modal/Modal';
import Button from 'ui3/Button/Button';
import Switch from 'ui3/Switch/Switch';
import Input from 'ui3/Input/Input';
import NotificationModal from 'ui3/NotificationModal/NotificationModal';
// Components
import { showToast } from 'components/common/Toast/Toast';
// Styles
import styles from './AddPaymentModal.module.scss';

const validationSchema = yup.object().shape({
  cardHolderName: yupStringRequiredValidation('Cardholder name'),
  isDefault: yup.boolean(),
});

const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  isOpen,
  userEmail,
  onClose,
}) => {
  const [
    addStripePaymentMethod,
    { loading: addStripePaymentMethodLoading },
  ] = useMutation<AddStripePaymentMethod, AddStripePaymentMethodVariables>(
    ADD_STRIPE_PAYMENT_METHOD,
    {
      update(cache, { data }) {
        cache.modify({
          fields: {
            myPaymentMethods(existingPaymentMethods = []) {
              const newPaymentMethodRef = cache.writeFragment({
                data: data?.addStripePaymentMethod,
                fragment: PaymentFields,
              });

              return [...existingPaymentMethods, newPaymentMethodRef];
            },
          },
        });
      },
      refetchQueries: [MY_PAYMENT_METHODS],
    }
  );

  const [
    setPaymentMethodAsDefault,
    { loading: setAsDefaultLoading },
  ] = useMutation<
    SetPaymentMethodAsDefault,
    SetPaymentMethodAsDefaultVariables
  >(SET_PAYMENT_METHOD_AS_DEFAULT, {
    refetchQueries: [MY_PAYMENT_METHODS],
  });

  const [isModalVisible, setModalVisibility] = useState<boolean>(false);
  const [successModalIsVisible, setSuccessModalIsVisible] = useState<boolean>(
    false
  );
  const [stripeRequestIsLoading, setStripeRequestIsLoading] = useState<boolean>(
    false
  );

  useEffect(() => {
    setModalVisibility(isOpen);
  }, [isOpen]);

  const stripe = useStripe();
  const elements = useElements();

  const {
    register,
    handleSubmit,
    errors: formErrors,
  } = useForm<PaymentFormInputs>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      cardHolderName: '',
      isDefault: true,
    },
  });

  const showError = (message: string) => {
    setStripeRequestIsLoading(false);
    showToast({
      message,
      type: 'error',
    });
  };

  const handleFormSubmit: SubmitHandler<PaymentFormInputs> = async (
    values
  ): Promise<void> => {
    const { cardHolderName, isDefault } = values;
    try {
      const cardElement = elements?.getElement(CardNumberElement);

      if (stripe && cardElement) {
        setStripeRequestIsLoading(true);
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: cardHolderName,
            email: userEmail,
          },
        });

        if (error) {
          return showError(error?.message || '');
        }

        if (paymentMethod?.id) {
          const { data } = await addStripePaymentMethod({
            variables: { id: paymentMethod?.id },
          });
          const id = data?.addStripePaymentMethod?.id;

          if (isDefault && id) {
            await setPaymentMethodAsDefault({
              variables: { id },
            });
          }

          setModalVisibility(false);
          setSuccessModalIsVisible(true);
        }

        setStripeRequestIsLoading(false);
      }
    } catch (error) {
      showError(
        error?.message ||
          'Looks like something went wrong. Double check all fields and try again'
      );
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    setModalVisibility(false);
    setSuccessModalIsVisible(false);
  };

  return (
    <>
      <Modal
        size="medium"
        open={isModalVisible}
        onClose={handleClose}
        focusTrapped={false}
        blockScroll={false}
        showCloseIcon={false}
        containerClassName={styles.addPaymentModalContainer}
      >
        <div className={styles.modalContent}>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className={styles.header}>
              <h2 className={styles.title}>Add New Card</h2>
              <button
                type="button"
                className={styles.closeButton}
                onClick={handleClose}
                aria-label="Close"
              >
                <MillionsIcon name="close" className={styles.closeIcon} />
              </button>
            </div>

            <div className={styles.addPaymentBody}>
              <Input
                name="cardHolderName"
                type="text"
                ref={register}
                placeholder="First Last name"
                error={formErrors?.cardHolderName?.message}
                label="Cardholder Name"
              />

              <div className={styles.stripeField}>
                <label htmlFor="cardNumber" className={styles.stripeLabel}>
                  Card Number
                </label>
                <div className={styles.stripeInputWrapper}>
                  <CardNumberElement
                    id="cardNumber"
                    options={getStripeElementOptions('1234 1234 1234 1234')}
                    className={styles.addPaymentElementInput}
                  />
                </div>
              </div>

              <div className={styles.addPaymentElementsGroup}>
                <div className={styles.stripeField}>
                  <label htmlFor="expireDate" className={styles.stripeLabel}>
                    Expire Date
                  </label>
                  <div className={styles.stripeInputWrapper}>
                    <CardExpiryElement
                      id="expireDate"
                      options={getStripeElementOptions('MM/YY')}
                      className={styles.addPaymentElementInput}
                    />
                  </div>
                </div>

                <div className={styles.stripeField}>
                  <label htmlFor="cvv" className={styles.stripeLabel}>
                    CVV
                  </label>
                  <div className={styles.stripeInputWrapper}>
                    <CardCvcElement
                      id="cvv"
                      options={getStripeElementOptions('CVV')}
                      className={styles.addPaymentElementInput}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.addPaymentFooter}>
              <Switch
                name="isDefault"
                ref={register}
                label="Set as default"
                boldLabel
                wrapperClassName={styles.switchReversed}
              />

              <Button
                type="submit"
                size="medium"
                className={styles.addPaymentCta}
                loading={
                  addStripePaymentMethodLoading ||
                  stripeRequestIsLoading ||
                  setAsDefaultLoading
                }
              >
                Add Card
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      <NotificationModal
        open={successModalIsVisible}
        type="success"
        highlightedTitle="Well Done"
        title="You've successfully added a new card"
        onClose={handleClose}
        acceptLabel="Done"
        onAcceptClick={handleClose}
      />
    </>
  );
};

export default AddPaymentModal;
